import { type FC } from "react";

// WHAT: Detailseite für Oura-Readiness-Daten mit Score, Contributor-Analyse und Temperatur-Trends
// WHY:  Erlaubt eine gezielte Bewertung der Tagesbereitschaft unter Einbezug von Faktoren wie Erholung, Belastung und Temperatur
// DOMAIN: health → oura → pages
// DEPENDENCIES: `useOptimizedReadiness`, TracQ Card/Badge/Progress-Bar
// RISKS:
//   - NOTE: Historische Entwicklung ist aktuell nicht integriert; Fokus liegt auf dem aktuellen Tag und seinen Faktoren
import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { ProgressBar } from "@/components/ui/Progress";
import { useOptimizedReadiness } from "@/domains/health/oura/hooks/useOptimizedReadiness";

/**
 * WHAT:
 *   Zeigt den Readiness-Score eines Tages mit Status-Badge, detaillierter Aufschlüsselung der Contributors
 *   und Temperatur-Abweichungen/-Trends.
 *
 * WHY:
 *   Unterstützt Entscheidungen zu Trainingsintensität und Belastungssteuerung basierend auf Oura-Daten.
 *
 * @returns Readiness detail component
 *
 */
export const ReadinessDetail: FC = () => {
  const { error, loading, readiness, score: readinessScore } = useOptimizedReadiness();

  if (loading || error) {
    return null; // OuraApp handles loading/error states globally
  }
  if (!readiness) {
    return null; // OuraApp handles missing data states globally
  }

  const contributors = readiness.contributors;
  const temperatureDeviation = readiness.temperature_deviation;
  const temperatureTrendDeviation = readiness.temperature_trend_deviation;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-neutral-100 mb-2">Readiness Score</h2>
        <p className="text-neutral-400">Wie bereit bist du für den Tag?</p>
      </div>

      {/* Main Score */}
      <Card className="text-center" padding="lg" variant="default">
        <div className="text-6xl font-bold text-neutral-100 mb-2">{readinessScore}</div>
        <div className="text-neutral-400 mb-4">von 100</div>

        {/* Score Status */}
        <div className="mt-4">
          {readinessScore >= 80 && (
            <Badge className="text-sm" variant="success">
              🟢 Ausgezeichnet
            </Badge>
          )}
          {readinessScore >= 60 && readinessScore < 80 && (
            <Badge className="text-sm" variant="info">
              🔵 Gut
            </Badge>
          )}
          {readinessScore >= 40 && readinessScore < 60 && (
            <Badge className="text-sm" variant="warning">
              🟡 Fair
            </Badge>
          )}
          {readinessScore < 40 && (
            <Badge className="text-sm" variant="destructive">
              🔴 Schlecht
            </Badge>
          )}
        </div>
      </Card>

      {/* Contributors */}
      {Object.keys(contributors).length > 0 && (
        <Card padding="lg" variant="default">
          <h3 className="text-lg font-semibold text-neutral-200 mb-4">Faktoren</h3>
          <div className="grid gap-4">
            {Object.entries(contributors).map(([factor, score]) => {
              const scoreValue = score ?? 0;
              return (
                <div className="space-y-2" key={factor}>
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-300 capitalize">
                      {factor.replaceAll("_", " ")}
                    </span>
                    <span className="text-neutral-400 text-sm">{scoreValue}%</span>
                  </div>
                  <ProgressBar size="sm" state="default" value={scoreValue} />
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Temperature Data */}
      {(temperatureDeviation !== undefined || temperatureTrendDeviation !== undefined) && (
        <Card padding="lg" variant="default">
          <h3 className="text-lg font-semibold text-neutral-200 mb-4">Temperatur-Daten</h3>
          <div className="grid gap-4">
            {temperatureDeviation !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-neutral-300">Temperatur-Abweichung</span>
                <Badge
                  className="font-medium"
                  variant={(() => {
                    const absDev = Math.abs(temperatureDeviation);
                    if (absDev < 0.5) {
                      return "success";
                    }
                    if (absDev < 1) {
                      return "warning";
                    }
                    return "destructive";
                  })()}
                >
                  {temperatureDeviation > 0 ? "+" : ""}
                  {temperatureDeviation.toFixed(2)}°C
                </Badge>
              </div>
            )}
            {temperatureTrendDeviation !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-neutral-300">Temperatur-Trend</span>
                <Badge
                  className="font-medium"
                  variant={(() => {
                    const absDev = Math.abs(temperatureTrendDeviation);
                    if (absDev < 0.5) {
                      return "success";
                    }
                    if (absDev < 1) {
                      return "warning";
                    }
                    return "destructive";
                  })()}
                >
                  {temperatureTrendDeviation > 0 ? "+" : ""}
                  {temperatureTrendDeviation.toFixed(2)}°C
                </Badge>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Insights */}
      <Card padding="lg" variant="default">
        <h3 className="text-lg font-semibold text-neutral-200 mb-4">Insights</h3>
        <div className="space-y-3 text-neutral-300">
          {readinessScore >= 80 && (
            <p>🎯 Du bist optimal vorbereitet für den Tag. Perfekt für intensive Aktivitäten!</p>
          )}
          {readinessScore >= 60 && readinessScore < 80 && (
            <p>✅ Gute Basis für moderate Aktivitäten. Achte auf ausreichend Erholung.</p>
          )}
          {readinessScore >= 40 && readinessScore < 60 && (
            <p>⚠️ Moderate Readiness. Leichte Aktivitäten sind empfehlenswert.</p>
          )}
          {readinessScore < 40 && (
            <p>🔄 Niedrige Readiness. Fokus auf Erholung und leichte Aktivitäten.</p>
          )}
        </div>
      </Card>
    </div>
  );
};

// Named export only - no default export
